import React from 'react';
import {
  Box,
  Heading,
  Text,
  Icon,
  Flex,
  Button,
  SimpleGrid,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  HStack,
} from '@chakra-ui/react';
import { FaTrophy, FaChartLine, FaExternalLinkAlt, FaGem } from 'react-icons/fa';
import BaseBountyPool from './BaseBountyPool';
import { JACKPOT_MILLIONS_CONFIG } from '../Games/JackpotMillions/JackpotMillionsConfig';

/**
 * JackpotMillionsBountyPool Component
 * 
 * Custom bounty pool implementation for the Jackpot Millions scratch card game.
 * Extends the BaseBountyPool with Jackpot Millions specific styling and data.
 */
const JackpotMillionsBountyPool = () => {
  // Get bounty pool data from Jackpot Millions config
  const { bountyPool } = JACKPOT_MILLIONS_CONFIG;
  
  // Jackpot Millions specific styling
  const jackpotMillionsStyles = {
    accentColor: "pink.400",
    bgColor: "pink.900", // Pink background to match the Jackpot Millions theme
  };
  
  // Jackpot Millions specific header
  const renderHeader = () => (
    <Flex align="center" mb={4}>
      <Icon as={FaTrophy} boxSize={8} color="yellow.400" mr={3} />
      <Box>
        <Heading as="h3" size="lg" color="yellow.400">
          JACKPOT MILLIONS Bounty Pool
        </Heading>
        <Text color="gray.300" fontSize="sm">
          Premium scratch card with massive prizes up to 250,000 JINU!
        </Text>
      </Box>
    </Flex>
  );
  
  // Jackpot Millions specific stats
  const renderStats = () => (
    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5} my={6}>
      <Stat
        bg="rgba(0,0,0,0.3)"
        p={4}
        borderRadius="md"
        borderLeft="4px solid"
        borderColor="yellow.400"
      >
        <StatLabel color="gray.300">Total Prize Pool</StatLabel>
        <StatNumber color="white" fontSize="2xl">{bountyPool.totalPrizePool.toLocaleString()} JINU</StatNumber>
        <StatHelpText color="gray.400">Massive rewards await!</StatHelpText>
      </Stat>
      
      <Stat
        bg="rgba(0,0,0,0.3)"
        p={4}
        borderRadius="md"
        borderLeft="4px solid"
        borderColor="pink.400"
      >
        <StatLabel color="gray.300">Return to Player</StatLabel>
        <StatNumber color="white" fontSize="2xl">{bountyPool.rtp}%</StatNumber>
        <StatHelpText color="gray.400">Higher RTP than other games</StatHelpText>
      </Stat>
      
      <Stat
        bg="rgba(0,0,0,0.3)"
        p={4}
        borderRadius="md"
        borderLeft="4px solid"
        borderColor="green.400"
      >
        <StatLabel color="gray.300">Jackpot Prize</StatLabel>
        <StatNumber color="white" fontSize="2xl">250,000 JINU</StatNumber>
        <StatHelpText color="gray.400">
          <HStack>
            <Text>Exclusive top prize</Text>
            <Badge colorScheme="green">Premium</Badge>
          </HStack>
        </StatHelpText>
      </Stat>
    </SimpleGrid>
  );
  
  // Jackpot Millions specific footer with other games showcase
  const renderFooter = () => (
    <Box mt={8}>
      <Heading as="h4" size="md" mb={4} color="white">
        Try Your Luck with Other Games
      </Heading>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5}>
        <Box 
          bg="black" 
          p={4} 
          borderRadius="md" 
          boxShadow="md"
          _hover={{ transform: "translateY(-5px)", transition: "transform 0.3s" }}
        >
          <Text fontWeight="bold" color="yellow.400" mb={2}>QUICK CASH</Text>
          <Text color="gray.300" fontSize="sm" mb={3}>Win up to 50,000 JINU</Text>
          <Button size="sm" colorScheme="yellow" variant="outline" rightIcon={<FaExternalLinkAlt />}>
            Play Now
          </Button>
        </Box>
        <Box 
          bg="green.900" 
          p={4} 
          borderRadius="md" 
          boxShadow="md"
          _hover={{ transform: "translateY(-5px)", transition: "transform 0.3s" }}
        >
          <Text fontWeight="bold" color="yellow.400" mb={2}>CASH KING</Text>
          <Text color="gray.300" fontSize="sm" mb={3}>Win up to 100,000 JINU</Text>
          <Button size="sm" colorScheme="yellow" variant="outline" rightIcon={<FaExternalLinkAlt />}>
            Play Now
          </Button>
        </Box>
        <Box 
          bg="blue.900" 
          p={4} 
          borderRadius="md" 
          boxShadow="md"
          _hover={{ transform: "translateY(-5px)", transition: "transform 0.3s" }}
        >
          <Text fontWeight="bold" color="yellow.400" mb={2}>MEGA JINU SCRATCH</Text>
          <Text color="gray.300" fontSize="sm" mb={3}>Win up to 10,000,000 JINU</Text>
          <Button size="sm" colorScheme="yellow" variant="outline" rightIcon={<FaExternalLinkAlt />}>
            Coming Soon
          </Button>
        </Box>
      </SimpleGrid>
    </Box>
  );
  
  return (
    <Box>
      {renderHeader()}
      {renderStats()}
      <BaseBountyPool 
        poolData={bountyPool}
        {...jackpotMillionsStyles}
      />
      {renderFooter()}
    </Box>
  );
};

export default JackpotMillionsBountyPool;
